/**
 * ConfirmDialog.jsx
 * ─────────────────
 * Styled in-app confirmation modal — replaces native `window.confirm()`
 * which can't be styled, can't render rich content, and is blocked by
 * Playwright's default browser context.
 *
 * Dismisses via Cancel button, Close (X) button, or outside-overlay
 * click. The `confirmTone` prop renders the primary action in danger
 * red for destructive flows.
 *
 * Test selectors are derived from the `testid` prop:
 *   <ConfirmDialog testid="my-action" />  →
 *     [data-testid="my-action"]              (modal root)
 *     [data-testid="my-action-close"]        (X button)
 *     [data-testid="my-action-cancel"]       (Cancel button)
 *     [data-testid="my-action-confirm"]      (primary action)
 *
 * Usage:
 *   const [pending, setPending] = useState(null);
 *   ...
 *   <button onClick={() => setPending({ /* config * / })}>Delete</button>
 *   {pending && (
 *     <ConfirmDialog
 *       title="Delete record?"
 *       body="This cannot be undone."
 *       confirmLabel="Delete"
 *       confirmTone="danger"
 *       busy={busy}
 *       onCancel={() => setPending(null)}
 *       onConfirm={async () => { await doDelete(); setPending(null); }}
 *     />
 *   )}
 */
import { X } from "lucide-react";

export default function ConfirmDialog({
  title,
  body,
  confirmLabel = "Continue",
  confirmTone = "primary",   // "primary" | "danger"
  busy = false,
  onConfirm,
  onCancel,
  testid = "confirm-dialog",
}) {
  return (
    <div
      data-testid={testid}
      onClick={onCancel}
      style={{
        position: "fixed", inset: 0,
        // Iter 320.1 (Feb 2026) — raised from 200 → 9999 so the confirm
        // dialog reliably floats above every existing high-z overlay
        // (SegmentBuilder drawer at 9500, segment-matches panel at
        // 9600, tour spotlights, etc). Previously "Delete segment"
        // rendered *behind* the SegmentBuilder's blurred backdrop,
        // forcing users to close the drawer to interact with it.
        background: "rgba(0,0,0,0.5)", zIndex: 9999,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--surface)", borderRadius: 12,
          width: 480, maxHeight: "85vh", overflow: "auto",
          padding: "22px 24px",
          boxShadow: "0 12px 40px rgba(0,0,0,0.18)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>{title}</h3>
          <button
            onClick={onCancel}
            data-testid={`${testid}-close`}
            style={{ background: "none", border: "none", cursor: "pointer", padding: 4, lineHeight: 0 }}
          >
            <X style={{ width: 14, height: 14, color: "var(--text-tertiary)" }} />
          </button>
        </div>
        <div style={{ fontSize: 12, color: "var(--text-secondary)", lineHeight: 1.55, marginBottom: 20 }}>
          {body}
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button
            className="btn"
            onClick={onCancel}
            disabled={busy}
            data-testid={`${testid}-cancel`}
            style={{ fontSize: 12, padding: "7px 14px" }}
          >
            Cancel
          </button>
          <button
            className={`btn btn-${confirmTone === "danger" ? "danger" : "primary"}`}
            onClick={onConfirm}
            disabled={busy}
            data-testid={`${testid}-confirm`}
            style={{
              fontSize: 12, padding: "7px 14px",
              opacity: busy ? 0.55 : 1,
              ...(confirmTone === "danger"
                ? { background: "var(--red)", borderColor: "var(--red)", color: "#fff" }
                : {}),
            }}
          >
            {busy ? "Working…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
